// Music theory for the adaptive score (§13.4): note names, scales, roman-numeral chords, voice-led
// voicings and the musical-bonk pentatonic. Pure and DOM-free (unit tested in Node).

export type ScaleName = 'major' | 'minor' | 'dorian' | 'mixolydian';

/** Semitone offsets of each scale degree from the tonic. */
export const SCALES: Readonly<Record<ScaleName, readonly number[]>> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
};

/** §13.4 musical bonks: semitones above the key root (octave 4) for chain steps 0..7+. */
export const BONK_DEGREES: readonly number[] = [0, 2, 4, 7, 9, 12, 14, 16];
/** The same climb in a minor (or dorian) key: the minor pentatonic, so no major 3rd clashes. */
export const BONK_DEGREES_MINOR: readonly number[] = [0, 3, 5, 7, 10, 12, 15, 17];

const LETTERS: Readonly<Record<string, number>> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const NOTE_RE = /^([A-Ga-g])(#{1,2}|b{1,2})?(-?\d)$/;
const SHARP_NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];

/** 'C4' → 60, 'Bb2' → 46, 'G#1' → 32. Throws on malformed names. */
export function noteToMidi(name: string): number {
  const m = NOTE_RE.exec(name);
  if (!m) throw new Error(`bad note name '${name}'`);
  const letter = LETTERS[m[1].toUpperCase()];
  const acc = m[2] ?? '';
  const shift = acc.startsWith('#') ? acc.length : -acc.length;
  return (Number(m[3]) + 1) * 12 + letter + shift;
}

export function isNoteName(s: string): boolean {
  return NOTE_RE.test(s);
}

export function midiName(m: number): string {
  const r = Math.round(m);
  return `${SHARP_NAMES[((r % 12) + 12) % 12]}${Math.floor(r / 12) - 1}`;
}

export function midiToFreq(m: number): number {
  return 440 * 2 ** ((m - 69) / 12);
}

export function freqToMidi(f: number): number {
  return 69 + 12 * Math.log2(f / 440);
}

/** playbackRate that moves a buffer recorded at `fromMidi` to `toMidi`. */
export function rateFor(fromMidi: number, toMidi: number): number {
  return 2 ** ((toMidi - fromMidi) / 12);
}

export function pc(m: number): number {
  return ((Math.round(m) % 12) + 12) % 12;
}

export interface ChordSpec {
  /** Semitones from the key tonic to the chord root (0..11). */
  root: number;
  /** Chord tones in semitones above the chord root, ascending, root first. */
  intervals: number[];
  symbol: string;
}

const NUMERALS = ['VII', 'III', 'VI', 'IV', 'II', 'V', 'I'] as const;
const NUMERAL_DEGREE: Readonly<Record<string, number>> = { I: 0, II: 1, III: 2, IV: 3, V: 4, VI: 5, VII: 6 };

const MAJOR_QUALITIES: Readonly<Record<string, number[]>> = {
  '': [0, 4, 7], '7': [0, 4, 7, 10], maj7: [0, 4, 7, 11], '6': [0, 4, 7, 9], '9': [0, 4, 7, 10, 14], maj9: [0, 4, 7, 11, 14],
  '69': [0, 4, 7, 9, 14], sus4: [0, 5, 7], '7sus4': [0, 5, 7, 10], sus2: [0, 2, 7], add9: [0, 4, 7, 14], aug: [0, 4, 8], '+': [0, 4, 8],
  '13': [0, 4, 7, 10, 21], '7b9': [0, 4, 7, 10, 13], dim: [0, 3, 6], 'dim7': [0, 3, 6, 9], '°7': [0, 3, 6, 9],
};
const MINOR_QUALITIES: Readonly<Record<string, number[]>> = {
  '': [0, 3, 7], '7': [0, 3, 7, 10], '6': [0, 3, 7, 9], '9': [0, 3, 7, 10, 14], maj7: [0, 3, 7, 11], add9: [0, 3, 7, 14],
  dim: [0, 3, 6], '°': [0, 3, 6], dim7: [0, 3, 6, 9], '°7': [0, 3, 6, 9], 'ø7': [0, 3, 6, 10], 'ø': [0, 3, 6, 10], m7b5: [0, 3, 6, 10],
  '11': [0, 3, 7, 10, 17],
};

function splitNumeral(s: string): { acc: number; numeral: string; rest: string } {
  let i = 0;
  let acc = 0;
  while (s[i] === 'b' || s[i] === '#') {
    acc += s[i] === 'b' ? -1 : 1;
    i++;
  }
  const body = s.slice(i);
  for (const n of NUMERALS) {
    if (body.toUpperCase().startsWith(n)) {
      const numeral = body.slice(0, n.length);
      // Mixed case (e.g. 'Iv') is not a numeral.
      if (numeral !== n && numeral !== n.toLowerCase()) continue;
      return { acc, numeral, rest: body.slice(n.length) };
    }
  }
  throw new Error(`bad roman numeral '${s}'`);
}

/**
 * Parses a roman-numeral chord ('I', 'vi7', 'ii7', 'V7', 'IVmaj7', 'bVII', '#ivø7', 'V7/V').
 * Numerals are degrees of the song's scale (so in A minor, 'VI' is F major); a 'b'/'#' prefix
 * shifts the root chromatically. Case selects the triad quality (upper major, lower minor), the
 * suffix the extension. A secondary function 'X/Y' is X counted in the major scale of degree Y.
 */
export function parseRoman(symbol: string, scale: ScaleName): ChordSpec {
  const slash = symbol.indexOf('/');
  if (slash > 0) {
    const target = parseRoman(symbol.slice(slash + 1), scale);
    const inner = parseRoman(symbol.slice(0, slash), 'major');
    return { root: (target.root + inner.root) % 12, intervals: inner.intervals, symbol };
  }
  const { acc, numeral, rest } = splitNumeral(symbol);
  const upper = numeral === numeral.toUpperCase();
  const table = upper ? MAJOR_QUALITIES : MINOR_QUALITIES;
  const intervals = table[rest];
  if (!intervals) throw new Error(`bad chord quality '${rest}' in '${symbol}'`);
  const degree = NUMERAL_DEGREE[numeral.toUpperCase()];
  const root = (((SCALES[scale][degree] + acc) % 12) + 12) % 12;
  return { root, intervals: [...intervals], symbol };
}

/** Pitch classes (relative to the key tonic) of a chord. */
export function chordPitchClasses(c: ChordSpec): number[] {
  return c.intervals.map((i) => (c.root + i) % 12);
}

/**
 * Pitch classes (relative to the tonic) that sound consonant over a chord: its tones plus the
 * tensions it takes (the 9th unless diminished, the 11th on minor chords, the 6th/13th on major,
 * dominant and minor chords; never the 11th over a major 3rd).
 */
export function chordSafePcs(c: ChordSpec): Set<number> {
  const tones = new Set(chordPitchClasses(c));
  const iv = c.intervals;
  const minor = iv[1] === 3;
  const diminished = iv[2] === 6;
  if (!diminished) tones.add((c.root + 2) % 12);
  if (minor && !diminished) tones.add((c.root + 5) % 12);
  if (!diminished) tones.add((c.root + 9) % 12);
  return tones;
}

/**
 * Fits a melodic note to the chord under it: kept when consonant over the chord, else moved to the
 * nearest chord tone (then tension) within ±2 semitones, ties going in the melody's direction
 * `dir` (+1 up, −1 down). Diatonic chords keep a pentatonic note as it is.
 */
export function fitToChord(midi: number, tonicMidi: number, c: ChordSpec, dir = 1): number {
  const rel = (m: number): number => pc(m - tonicMidi);
  const safe = chordSafePcs(c);
  if (safe.has(rel(midi))) return midi;
  const tones = new Set(chordPitchClasses(c));
  const order = dir >= 0 ? [1, -1, 2, -2] : [-1, 1, -2, 2];
  for (const pool of [tones, safe]) for (const d of order) if (pool.has(rel(midi + d))) return midi + d;
  return midi;
}

/** True when a chord borrows a tone from outside the scale (♭II7, iv in major, V7 in minor…). */
export function isChromatic(c: ChordSpec, scale: ScaleName): boolean {
  return chordPitchClasses(c).some((p) => !inScale(p, scale));
}

/** True if pitch class `p` (relative to the tonic) is in the scale. */
export function inScale(p: number, scale: ScaleName): boolean {
  return SCALES[scale].includes(((p % 12) + 12) % 12);
}

/** Pitch-class set of a mode starting at semitone offset `rootPc`. */
export function scalePitchClasses(scale: ScaleName): number[] {
  return [...SCALES[scale]];
}

export type VoicingStyle = 'close' | 'rootless' | 'spread' | 'triad';

/** The tones a voicing style keeps (in semitones above the chord root, < 24). */
function voicingTones(c: ChordSpec, style: VoicingStyle): number[] {
  const iv = c.intervals.map((i) => i % 12);
  const third = iv[1];
  const fifth = iv[2];
  const seventh = iv.length > 3 ? iv[3] : undefined;
  const ext = iv.length > 4 ? iv[4] : undefined;
  if (style === 'triad') return [0, third, fifth];
  if (style === 'rootless') {
    // Jazz rootless (3-5-7-9): the guide tones 3 and 7 carry the harmony, the bass has the root.
    // Triads keep their root; diminished colours take the root instead of a (clashing) 9th.
    if (seventh === undefined) return [0, third, fifth];
    const colour = ext !== undefined ? ext % 12 : fifth === 6 ? 0 : 2;
    return [third, fifth, seventh, colour].filter((v, i, a) => a.indexOf(v) === i);
  }
  const tones = [0, third, fifth];
  if (seventh !== undefined) tones.push(seventh);
  if (ext !== undefined && style === 'spread') tones.push(ext);
  return tones;
}

/** All voicings of `tones` (pitch classes above `rootMidi`) as ascending stacks inside [lo, hi]. */
function candidates(rootMidi: number, tones: readonly number[], lo: number, hi: number): number[][] {
  const out: number[][] = [];
  const n = tones.length;
  for (let inv = 0; inv < n; inv++) {
    for (let base = lo - 12; base <= hi; base += 12) {
      const stack: number[] = [];
      let prev = -Infinity;
      for (let k = 0; k < n; k++) {
        const pcAbove = tones[(inv + k) % n];
        let m = rootMidi + pcAbove + Math.floor((base - rootMidi - pcAbove) / 12) * 12;
        while (m < base) m += 12;
        while (m <= prev) m += 12;
        stack.push(m);
        prev = m;
      }
      if (stack[0] >= lo && stack[stack.length - 1] <= hi) out.push(stack);
    }
  }
  return out;
}

/**
 * A voice-led voicing of chord `c` in a key whose tonic is `tonicMidi`: of all inversions inside
 * [lo, hi], the one that moves least from `prev` (or sits nearest the range centre). Spread
 * voicings open the stack by dropping the second voice from the top an octave (drop-2).
 */
export function voiceChord(c: ChordSpec, tonicMidi: number, style: VoicingStyle, lo: number, hi: number, prev: readonly number[] | null): number[] {
  const tones = voicingTones(c, style);
  const rootMidi = tonicMidi + c.root;
  let cands = candidates(rootMidi, tones, lo, hi);
  if (style === 'spread') {
    cands = cands.map((s) => {
      if (s.length < 3) return s;
      const d = [...s];
      d[d.length - 2] -= 12;
      return d.sort((a, b) => a - b);
    }).filter((s) => s[0] >= lo - 5);
  }
  if (cands.length === 0) cands = candidates(rootMidi, tones, lo - 12, hi + 12);
  const centre = (lo + hi) / 2;
  let best = cands[0];
  let bestCost = Infinity;
  for (const s of cands) {
    let cost: number;
    if (prev && prev.length > 0) {
      cost = 0;
      for (const m of s) cost += Math.min(...prev.map((p) => Math.abs(p - m)));
      // Keep the top voice smooth: it is the one the ear follows.
      cost += 0.5 * Math.abs(s[s.length - 1] - prev[prev.length - 1]);
      // Do not drift away from the register.
      cost += 0.15 * Math.abs((s[0] + s[s.length - 1]) / 2 - centre);
    } else {
      cost = Math.abs((s[0] + s[s.length - 1]) / 2 - centre);
    }
    if (cost < bestCost) {
      bestCost = cost;
      best = s;
    }
  }
  return best;
}

/** MIDI of the `degree`-th chord tone ('1','3','5','7','8','9','10','12'…) above the chord root. */
export function chordDegree(c: ChordSpec, tonicMidi: number, degree: number): number {
  const root = tonicMidi + c.root;
  const iv = c.intervals;
  const third = iv[1];
  const fifth = iv[2];
  const seventh = iv.length > 3 ? iv[3] % 12 : 12;
  switch (degree) {
    case 1: return root;
    case 2: return root + 2;
    case 3: return root + third;
    case 4: return root + 5;
    case 5: return root + fifth;
    case 6: return root + 9;
    case 7: return root + seventh;
    case 8: return root + 12;
    case 9: return root + 14;
    case 10: return root + 12 + third;
    case 11: return root + 17;
    case 12: return root + 12 + fifth;
    case 13: return root + 21;
    case 14: return root + 12 + seventh;
    case 15: return root + 24;
    default: return root + (degree > 0 ? (degree - 1) * 2 : 0);
  }
}

/** The MIDI note of the key root at octave 4 (C4..B4) for a tonic pitch class offset. */
export function rootAtOctave4(tonicMidi: number): number {
  return 60 + pc(tonicMidi);
}

/**
 * Index into the chain degrees for chain step `step`. Steps 0..7 climb (§13.4); a brawl busy enough
 * to keep chaining past the top turns around and comes back down (0..7..1, 0..7..1, ...), so the
 * phrase keeps moving instead of pinning every bonk to the top note. A KO still resolves it.
 */
export function chainIndex(step: number): number {
  const top = BONK_DEGREES.length - 1;
  const s = Math.max(0, Math.floor(step));
  if (s <= top) return s;
  const period = 2 * top;
  const m = s % period;
  return m <= top ? m : period - m;
}

/** Semitones above the key root of chain step `step` (major or minor pentatonic). */
export function chainDegree(step: number, minor = false): number {
  return (minor ? BONK_DEGREES_MINOR : BONK_DEGREES)[chainIndex(step)];
}

/** Pitch of chain step `step` of the musical bonks over the current key. */
export function bonkPitch(tonicMidi: number, step: number, minor = false): number {
  return rootAtOctave4(tonicMidi) + chainDegree(step, minor);
}

/** True for the scales whose pentatonic is minor. */
export function isMinorScale(scale: ScaleName): boolean {
  return scale === 'minor' || scale === 'dorian';
}

/** Nearest MIDI note to `m` among those with pitch class `p` (0..11). */
export function nearestWithPc(m: number, p: number): number {
  const base = Math.round(m);
  let best = base;
  let bestD = Infinity;
  for (let d = -6; d <= 6; d++) {
    if (pc(base + d) === pc(p) && Math.abs(d) < bestD) {
      bestD = Math.abs(d);
      best = base + d;
    }
  }
  return best;
}
